import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame, MapPin, PhoneCall, ShieldCheck, Snowflake, Star, Timer } from "lucide-react";

const LINES = [
  { text: "MASTER EVERY", gradient: false },
  { text: "DEGREE OF", gradient: true },
  { text: "COMFORT.", gradient: false },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero({ onQuote }) {
  const [mode, setMode] = useState("forge");
  const cool = mode === "ice";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden pb-20 pt-32">
      <div className="bg-blueprint pointer-events-none absolute inset-0" />
      <motion.div
        style={{ y: yGlow }}
        className={`pointer-events-none absolute -top-40 right-[-15%] h-[640px] w-[640px] rounded-full blur-[140px] transition-colors duration-1000 ${
          cool ? "bg-ice/15" : "bg-forge/15"
        }`}
      />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-ice/10 blur-[120px]" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-forge" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
              Emergency Dispatch — Same-Day Service
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            aria-label="Master every degree of comfort."
            className="font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-50 sm:text-5xl lg:text-[4.6rem]"
          >
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${
                    line.gradient
                      ? "bg-gradient-to-r from-ice via-slate-100 to-forge bg-clip-text text-transparent"
                      : ""
                  }`}
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.2 + i * 0.14, duration: 0.95, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            Here at Crowson, we offer the best, whether residential or
            commercial, our high standards bring us valued customers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-btn-quote"
              onClick={onQuote}
              className="group flex items-center gap-2.5 rounded-full bg-gradient-to-br from-forge to-forge-hi px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white shadow-[0_12px_40px_rgba(255,107,0,0.4)] transition-transform duration-200 hover:scale-[1.04] active:scale-95"
            >
              Get Instant Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-btn-call"
              href="tel:+16155932628"
              className="flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-slate-100 backdrop-blur transition-colors duration-200 hover:border-ice/50 hover:text-ice"
            >
              <PhoneCall className="h-4 w-4" />
              615-593-2628
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3"
          >
            {[
              { icon: MapPin, text: "Nashville, TN & Surrounding Areas" },
              { icon: ShieldCheck, text: "100% Warrantied" },
              { icon: Timer, text: "Same-Day Service" },
              { icon: Star, text: "4.9 / 5 · 2,300 Reviews" },
            ].map(({ icon: Icon, text }) => (
              <span key={text} className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                <Icon className="h-3.5 w-3.5 text-forge" />
                {text}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1, ease: EASE }}
            className="relative"
          >
            <div
              className={`absolute -inset-5 rounded-[2rem] blur-3xl transition-colors duration-1000 ${
                cool ? "bg-ice/20" : "bg-forge/20"
              }`}
            />
            <motion.div style={{ y: yImg }} className="clip-frame relative overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/5463581/pexels-photo-5463581.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Crowson technician calibrating a residential HVAC system"
                className="h-[420px] w-full object-cover sm:h-[500px]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-navy-950/10 to-transparent" />
              <div className="absolute bottom-4 left-5 font-mono text-[9px] uppercase tracking-[0.24em] text-slate-300">
                TECH 04 — SYSTEM CALIBRATION · RIVER DISTRICT
              </div>
            </motion.div>

            <div
              data-testid="hero-temp-toggle"
              className="absolute -left-3 top-6 flex items-center gap-1 rounded-2xl border border-white/10 bg-navy-900/90 p-1.5 shadow-2xl backdrop-blur-xl sm:-left-6"
            >
              <button
                data-testid="hero-mode-ice"
                onClick={() => setMode("ice")}
                className={`flex items-center gap-2 rounded-xl px-3.5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                  cool ? "bg-gradient-to-br from-ice-lo to-ice text-navy-950" : "text-slate-400 hover:text-ice"
                }`}
              >
                <Snowflake className="h-3.5 w-3.5" />
                Ice 68°F
              </button>
              <button
                data-testid="hero-mode-forge"
                onClick={() => setMode("forge")}
                className={`flex items-center gap-2 rounded-xl px-3.5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                  !cool ? "bg-gradient-to-br from-forge to-forge-hi text-white" : "text-slate-400 hover:text-forge"
                }`}
              >
                <Flame className="h-3.5 w-3.5" />
                Forge 74°F
              </button>
            </div>

            <div className="absolute -bottom-5 right-4 rounded-2xl border border-white/10 bg-navy-900/90 px-5 py-3.5 shadow-2xl backdrop-blur-xl">
              <div className="font-display text-xl font-extrabold text-slate-50">15,000+</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Homes climate-locked</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
